import React, { useState } from 'react';
import { FaQuoteLeft, FaChevronDown } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

export default function FAQSection() {
    const [openFaq, setOpenFaq] = useState(null);

    const faqs = [
        {
            id: 1,
            question: 'How do I enroll in a course?',
            answer: 'You can enroll by clicking on any course and following the simple registration process. We accept various payment methods and offer flexible payment plans.'
        },
        {
            id: 2,
            question: 'Are there any prerequisites?',
            answer: 'Prerequisites vary by course. Most beginner courses have no prerequisites, while advanced courses may require basic knowledge. Check individual course descriptions for specific requirements.'
        },
        {
            id: 3,
            question: 'Can I get a certificate?',
            answer: 'Yes, certificates are provided upon successful completion of courses. Our certificates are recognized by industry leaders and can boost your career prospects.'
        },
        {
            id: 4,
            question: 'Is there student support available?',
            answer: 'We provide 24/7 student support through live chat, email, and phone. Our dedicated support team is always ready to help you with any questions or technical issues.'
        },
        {
            id: 5,
            question: 'What is the refund policy?',
            answer: 'We offer a 30-day money-back guarantee. If you\'re not satisfied with your course within the first 30 days, you can request a full refund.'
        }
    ];

    const toggleFaq = (id) => {
        setOpenFaq(openFaq === id ? null : id);
    };

    return (
        <section className="py-20 bg-white">
            <div className="max-w-6xl mx-auto px-6">
                {/* Modern Header */}
                <div className="text-center mb-16">
                    <div className="inline-block mb-6">
                        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-2xl px-6 py-3 shadow-sm">
                            <FaQuoteLeft className="text-blue-600 text-lg" />
                            <span className="text-blue-700 font-semibold text-sm lemon-milk">GOT QUESTIONS?</span>
                        </div>
                    </div>

                    <h2 className="text-4xl font-black text-gray-900 mb-4 lemon-milk">
                        Frequently Asked{' '}
                        <span className="text-blue-600">Questions</span>
                    </h2>

                    <p className="text-lg text-gray-600 max-w-2xl mx-auto poppins">
                        Everything you need to know about our mathematics classes, schedules, and enrollment process.
                    </p>
                </div>

                {/* FAQ Items */}
                <div className="space-y-6 max-w-4xl mx-auto">
                    {/* FAQ Item 1 */}
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                        <button
                            onClick={() => toggleFaq(1)}
                            className="w-full px-8 py-6 text-left hover:bg-gray-50 transition-colors flex items-center justify-between"
                        >
                            <span className="font-bold text-gray-900 text-lg lemon-milk">පන්ති හැකි කොහොමද?</span>
                            <div className={`w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center transition-transform duration-300 ${openFaq === 1 ? 'rotate-180' : ''}`}>
                                <FaChevronDown className="text-white text-sm" />
                            </div>
                        </button>
                        <AnimatePresence>
                            {openFaq === 1 && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="overflow-hidden"
                                >
                                    <div className="px-8 pb-6 bg-gray-50 border-t border-gray-100">
                                        <div className="pt-6">
                                            <p className="text-gray-700 leading-relaxed poppins">
                                                Online සහ Physical දෙකම පන්ති පැවැත්වේ. සියලුම Online පන්ති සදහා Sudeshmaths.lk වෙබ් අඩවිය හරහා පණිවිඩ වීමට හැකිය. Physical පන්ති පිළිබඳව කොන්දේසි,
                                                ඉඩකඩ සදහා,
                                                <br /><br />
                                                <strong>Hotline:</strong> 0772 700 701<br />
                                                <strong>Nugegoda:</strong> 0112 817 028<br />
                                                <strong>Gampaha:</strong> 0332 248 544<br />
                                                <strong>Smart Learnyn:</strong> 0117 109 690
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* FAQ Item 2 */}
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                        <button
                            onClick={() => toggleFaq(2)}
                            className="w-full px-8 py-6 text-left hover:bg-gray-50 transition-colors flex items-center justify-between"
                        >
                            <span className="font-bold text-gray-900 text-lg lemon-milk">නිමන්තන සම්බන්ධව ගැටලු</span>
                            <div className={`w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center transition-transform duration-300 ${openFaq === 2 ? 'rotate-180' : ''}`}>
                                <FaChevronDown className="text-white text-sm" />
                            </div>
                        </button>
                        <AnimatePresence>
                            {openFaq === 2 && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="overflow-hidden"
                                >
                                    <div className="px-8 pb-6 bg-gray-50 border-t border-gray-100">
                                        <div className="pt-6">
                                            <p className="text-gray-700 leading-relaxed poppins">
                                                නිමන්තන සම්බන්ධ ගැටලු සදහා අපගේ සහාය කණ්ඩායම සමග සම්බන්ධ වන්න. අපි ඔබට ඉක්මනින් උදව් කරන්නෙමු.
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* FAQ Item 3 */}
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                        <button
                            onClick={() => toggleFaq(3)}
                            className="w-full px-8 py-6 text-left hover:bg-gray-50 transition-colors flex items-center justify-between"
                        >
                            <span className="font-bold text-gray-900 text-lg lemon-milk">ගාස්තුවන ගැටලු</span>
                            <div className={`w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center transition-transform duration-300 ${openFaq === 3 ? 'rotate-180' : ''}`}>
                                <FaChevronDown className="text-white text-sm" />
                            </div>
                        </button>
                        <AnimatePresence>
                            {openFaq === 3 && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="overflow-hidden"
                                >
                                    <div className="px-8 pb-6 bg-gray-50 border-t border-gray-100">
                                        <div className="pt-6">
                                            <p className="text-gray-700 leading-relaxed poppins">
                                                ගාස්තු සම්බන්ධ විස්තර සහ ගෙවීම් ක්‍රම පිළිබඳ වැඩි විස්තර සදහා අපගේ කාර්යාලය සමග සම්බන්ධ වන්න.
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Contact CTA */}
                <div className="mt-16 text-center">
                    <div className="bg-gray-50 rounded-2xl p-8">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4 lemon-milk">Still Have Questions?</h3>
                        <p className="text-gray-600 mb-6 poppins">Our team is here to help you with any questions about our mathematics classes.</p>
                        <button className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors lemon-milk">
                            Contact Us
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}
